import {
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  LightBulbIcon,
  Cog6ToothIcon,
  PresentationChartBarIcon,
  RocketLaunchIcon,
  SparklesIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import ReactConfetti from "react-confetti";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Notesedit01Icon } from "../icon-imports";
import { homeRoute } from "../utils/routeHelper";

type Tint = "yellow" | "blue" | "pink" | "sky" | "green" | "purple";

const TINT_CLASSES: Record<Tint, string> = {
  yellow: "bg-amber-50 text-amber-600",
  blue: "bg-blue-50 text-blue-600",
  pink: "bg-pink-50 text-pink-600",
  sky: "bg-sky-50 text-sky-600",
  green: "bg-emerald-50 text-emerald-600",
  purple: "bg-violet-50 text-violet-600",
};

const IconBadge: React.FC<{ tint: Tint; children: React.ReactNode }> = ({
  tint,
  children,
}) => (
  <div
    className={clsx(
      "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl",
      TINT_CLASSES[tint]
    )}
  >
    {children}
  </div>
);

const InfoCard: React.FC<{
  tint: Tint;
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
}> = ({ tint, icon, title, description, action }) => (
  <div className="flex flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
    <IconBadge tint={tint}>{icon}</IconBadge>
    <h3 className="mt-5 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-500">
      {description}
    </p>
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
      >
        {action.label}
        <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
    )}
  </div>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="mb-8 text-center text-2xl font-bold text-gray-900">
    {children}
  </h2>
);

const PaymentSuccessPage: React.FC = () => {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const subscriptionType = searchParams.get("subscriptionType");

  const plan =
    subscriptionType === "Copilot"
      ? "AskAlayna Pro"
      : subscriptionType === "Slides"
      ? "Slides Pro"
      : subscriptionType === "CopilotSlidesBundle"
      ? "Alayna AI Pro Bundle"
      : "Pro";

  const showAppCta =
    subscriptionType === "Copilot" ||
    subscriptionType === "CopilotSlidesBundle" ||
    (!subscriptionType);
  const showSlidesCta =
    subscriptionType === "Slides" ||
    subscriptionType === "CopilotSlidesBundle";

  return (
    <React.Fragment>
      {showConfetti && (
        <ReactConfetti recycle={false} numberOfPieces={400} gravity={0.15} />
      )}

      <div className="min-h-screen bg-gradient-to-b from-blue-50/60 via-white to-white">
        <div className="mx-auto max-w-5xl px-6 py-16 md:py-20">
          {/* Hero */}
          <div className="flex flex-col items-center text-center">
            <img
              onClick={() => {
                window.location.href =
                  import.meta.env.VITE_APP_URL || "https://www.alayna.us";
              }}
              loading="lazy"
              className="h-8 cursor-pointer"
              src={"/imgs/logos/askalayna.png"}
              alt="Alayna logo"
            />

            <div className="relative mt-10">
              {/* soft glow */}
              <div
                className="absolute inset-0 -z-10 rounded-full bg-blue-400/30 blur-2xl"
                aria-hidden
              />
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 shadow-lg shadow-blue-500/25">
                <SparklesIcon className="h-10 w-10 text-white" />
              </div>
            </div>

            <div className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
              <CheckCircleIcon className="h-4 w-4" />
              Payment successful
            </div>

            <h1 className="mt-5 text-4xl font-bold md:text-5xl">
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                Welcome to Alayna Pro!
              </span>{" "}
              🎉
            </h1>

            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-gray-600">
              Thank you for upgrading to{" "}
              <span className="font-semibold text-gray-900">{plan}</span>. Your
              commitment to enhancing education is what drives us — we're
              thrilled to have you in our community of educators making a real
              impact.
            </p>

            {/* Primary CTAs */}
            <div className="mt-10 flex w-full flex-col items-center justify-center gap-3 sm:flex-row">
              {showAppCta && (
                <button
                  type="button"
                  onClick={() => navigate(homeRoute())}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-8 py-3.5 text-base font-semibold text-white shadow-md shadow-blue-500/25 transition-all hover:shadow-lg hover:shadow-blue-500/30 sm:w-auto"
                >
                  Take me to the App
                  <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </button>
              )}

              {showSlidesCta && (
                <button
                  type="button"
                  onClick={() => window.open("https://slides.new")}
                  className={clsx(
                    "inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-3.5 text-base font-semibold transition-all sm:w-auto",
                    showAppCta
                      ? "border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50"
                      : "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-blue-500/25 hover:shadow-lg"
                  )}
                >
                  <PresentationChartBarIcon className="h-5 w-5" />
                  New Slides Presentation
                </button>
              )}
            </div>
          </div>

          {/* Perks */}
          <section className="mt-24">
            <SectionHeading>Your Pro perks</SectionHeading>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <InfoCard
                tint="yellow"
                icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
                title="Priority Support"
                description="Get your questions resolved faster with our priority support team."
              />
              <InfoCard
                tint="blue"
                icon={<RocketLaunchIcon className="h-6 w-6" />}
                title="Early Access to New Features"
                description="Be the first to try out new features before anyone else."
              />
              <InfoCard
                tint="pink"
                icon={<VideoCameraIcon className="h-6 w-6" />}
                title="Exclusive Webinars"
                description="Attend exclusive webinars with industry experts and fellow educators."
              />
            </div>
          </section>

          {/* Community */}
          <section className="mt-20">
            <SectionHeading>Stay connected with the community</SectionHeading>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <InfoCard
                tint="sky"
                icon={
                  <img
                    loading="lazy"
                    src={"/imgs/logos/facebook.png"}
                    className="h-6 w-6"
                    alt="Facebook logo"
                  />
                }
                title="Teacher Community Group"
                description="Join our Facebook group to connect with thousands of other educators."
                action={{
                  label: "Visit Group",
                  onClick: () =>
                    window.open(
                      "https://www.facebook.com/groups/alaynaforeducators/"
                    ),
                }}
              />
              <InfoCard
                tint="purple"
                icon={
                  <img
                    loading="lazy"
                    src={"/imgs/logos/tiktok.png"}
                    className="h-6 w-6"
                    alt="TikTok logo"
                  />
                }
                title="TikTok Page"
                description="Follow our TikTok page for the latest tips and updates."
                action={{
                  label: "View Page",
                  onClick: () => window.open("https://www.tiktok.com/@joinalayna"),
                }}
              />
              <InfoCard
                tint="green"
                icon={<Notesedit01Icon className="h-6 w-6" />}
                title="Use Cases"
                description="Explore real classroom use-cases and resources on AI in education."
                action={{
                  label: "Visit Use Cases",
                  onClick: () => window.open("https://alayna.ai/use-cases"),
                }}
              />
            </div>
          </section>

          {/* Support */}
          <section className="mt-20">
            <SectionHeading>Get support &amp; share feedback</SectionHeading>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <InfoCard
                tint="blue"
                icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
                title="Support Chat"
                description="Reach our team right inside the app for the fastest help."
              />
              <InfoCard
                tint="pink"
                icon={<EnvelopeIcon className="h-6 w-6" />}
                title="Email Us"
                description="Email support@alayna.us any time and we'll get back to you."
                action={{
                  label: "support@alayna.us",
                  onClick: () =>
                    window.open("mailto:support@alayna.us"),
                }}
              />
              <InfoCard
                tint="yellow"
                icon={<LightBulbIcon className="h-6 w-6" />}
                title="Feedback Forum"
                description="Share feedback, bug reports and feature ideas with our team."
              />
            </div>
          </section>

          {/* Footer note */}
          <div className="mt-20 flex flex-col items-center gap-3 border-t border-gray-100 pt-10 text-center">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Cog6ToothIcon className="h-4 w-4" />
              Manage your subscription anytime from your account settings.
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default PaymentSuccessPage;
